/** @jsxRuntime automatic */
/** @jsxImportSource react */
import React, { useEffect, useId, useRef, useState } from "react"
import { TbCode, TbListTree, TbArrowBackUp, TbArrowForwardUp } from "react-icons/tb"
import {
  formulaCompletions,
  formulaTemplate,
  KPI_AGGREGATES,
  parseKpiFormula,
  serializeKpiFormula,
} from "@/datox/kpi-formula-editor"
import "./kpi-formula-editor.css"

export function KpiModeTabs({ id, label, value, options, onChange, panelID }) {
  return (
    <div className="datox-react-dashboard__segmented" role="tablist" aria-label={label}>
      {options.map((option, index) => (
        <button
          type="button"
          role="tab"
          key={option.value}
          id={`${id}-${option.value}`}
          aria-controls={panelID}
          aria-selected={value === option.value}
          tabIndex={value === option.value ? 0 : -1}
          className={`datox-react-dashboard__segment ${value === option.value ? "datox-react-dashboard__segment--active" : ""}`}
          onClick={() => onChange(option.value)}
          onKeyDown={(event) => {
            if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return
            event.preventDefault()
            event.stopPropagation()
            const next =
              event.key === "Home"
                ? 0
                : event.key === "End"
                  ? options.length - 1
                  : (index + (event.key === "ArrowRight" ? 1 : -1) + options.length) % options.length
            onChange(options[next].value)
            document.getElementById(`${id}-${options[next].value}`)?.focus()
          }}
        >
          {option.icon}
          {option.label}
        </button>
      ))}
    </div>
  )
}

export function KpiExpressionEditor({ value, onChange, columns, labels }) {
  const input = useRef(null)
  const id = useId()
  const [suggestions, setSuggestions] = useState(null)
  const [active, setActive] = useState(0)
  const show = () => {
    const element = input.current
    setSuggestions(formulaCompletions(element.value, element.selectionStart, columns, element.selectionEnd))
    setActive(0)
  }
  useEffect(() => {
    setSuggestions(null)
  }, [columns.join("\u0000")])
  useEffect(() => {
    if (suggestions) document.getElementById(`${id}-${active}`)?.scrollIntoView?.({ block: "nearest" })
  }, [active])
  const insert = (option) => {
    const next = value.slice(0, suggestions.start) + option.text + value.slice(suggestions.end)
    if (next.length > 1000) return
    const position = suggestions.start + option.caret
    onChange(next)
    setSuggestions(null)
    requestAnimationFrame(() => {
      input.current?.focus()
      input.current?.setSelectionRange(position, position)
    })
  }
  return (
    <div className="datox-kpi-formula__expression">
      <div className="datox-kpi-formula__row">
        <label htmlFor={id}>{labels.expression}</label>
        <button
          type="button"
          className="datox-kpi-formula__link"
          onClick={() => {
            input.current.focus()
            show()
          }}
        >
          {labels.insertFunction}
        </button>
      </div>
      <textarea
        ref={input}
        id={id}
        className="datox-react-dashboard__input datox-kpi-formula__code"
        value={value}
        maxLength={1000}
        rows={4}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={!!suggestions}
        aria-controls={suggestions ? `${id}-options` : undefined}
        aria-activedescendant={suggestions?.options[active] ? `${id}-${active}` : undefined}
        aria-describedby={`${id}-help`}
        onBlur={() => setSuggestions(null)}
        onClick={() => setSuggestions(null)}
        onChange={(event) => {
          onChange(event.currentTarget.value)
          if (suggestions) {
            setSuggestions(formulaCompletions(event.currentTarget.value, event.currentTarget.selectionStart, columns))
            setActive(0)
          }
        }}
        onKeyDown={(event) => {
          if ((event.ctrlKey || event.metaKey) && (event.key === " " || event.code === "Space")) {
            event.preventDefault()
            event.stopPropagation()
            show()
            return
          }
          if (!suggestions) return
          if (event.key === "Escape") {
            event.preventDefault()
            event.stopPropagation()
            setSuggestions(null)
            return
          }
          if (suggestions.options.length && ["ArrowDown", "ArrowUp", "Enter", "Tab"].includes(event.key)) {
            event.preventDefault()
            event.stopPropagation()
            if (event.key === "Enter" || event.key === "Tab") {
              insert(suggestions.options[active])
              return
            }
            setActive(
              (index) =>
                (index + (event.key === "ArrowDown" ? 1 : -1) + suggestions.options.length) %
                suggestions.options.length,
            )
          }
        }}
      />
      <small id={`${id}-help`}>{labels.completionHelp}</small>
      {suggestions ? (
        <div
          className="datox-kpi-formula__suggestions"
          id={`${id}-options`}
          role="listbox"
          aria-label={labels.suggestions}
        >
          {!suggestions.options.length ? (
            <p role="status">{labels.noSuggestions}</p>
          ) : (
            suggestions.options.map((option, index) => (
              <button
                key={`${option.kind}:${option.label}`}
                type="button"
                role="option"
                aria-selected={index === active}
                id={`${id}-${index}`}
                tabIndex={-1}
                onPointerDown={(event) => event.preventDefault()}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => insert(option)}
                title={option.kind === "function" ? labels.functions[option.label] : labels.sourceFields}
              >
                <span className="datox-kpi-formula__suggestion-name">
                  <code>{option.label}</code>
                  <span>{option.kind === "function" ? labels.functionLabel : labels.columnLabel}</span>
                </span>
                {option.kind === "function" ? <small>{labels.functions[option.label]}</small> : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  )
}

function formulaDraft(expression, mode = "builder") {
  if (!expression) return { expression, tree: formulaTemplate("aggregate"), mode, incomplete: false }
  try {
    return { expression, tree: parseKpiFormula(expression), mode, incomplete: false }
  } catch {
    return { expression, tree: null, mode: "expression", incomplete: false }
  }
}

/** @param {{ value: string, onChange: (value: string) => void, columns?: string[], labels: ReturnType<typeof import("@/i18n/datox-kpi").dashboardKpiLabels> }} props */
export function KpiFormulaEditor({ value, onChange, columns = [], labels }) {
  const [draft, setDraft] = useState(() => formulaDraft(value))
  const [history, setHistory] = useState({ past: [], future: [] })
  const id = useId()
  useEffect(() => {
    if (value === draft.expression) return
    setDraft(formulaDraft(value, draft.mode))
    setHistory({ past: [], future: [] })
  }, [value])
  const edit = (next) => {
    setHistory((state) => ({ past: [...state.past.slice(-29), draft], future: [] }))
    setDraft(next)
    onChange(next.expression)
  }
  const changeTree = (tree) => {
    try {
      edit({ mode: "builder", tree, expression: serializeKpiFormula(tree), incomplete: false })
    } catch {
      edit({ mode: "builder", tree, expression: "", incomplete: true })
    }
  }
  const restore = (direction) => {
    const stack = history[direction]
    if (!stack.length) return
    const next = stack[stack.length - 1]
    setHistory(
      direction === "past"
        ? { past: stack.slice(0, -1), future: [...history.future, draft] }
        : { past: [...history.past, draft], future: stack.slice(0, -1) },
    )
    setDraft(next)
    onChange(next.expression)
  }
  return (
    <div className="datox-kpi-formula">
      <div className="datox-kpi-formula__toolbar">
        <KpiModeTabs
          id={id}
          label={labels.editorMode}
          panelID={`${id}-panel`}
          value={draft.mode}
          options={[
            { value: "builder", label: labels.builder, icon: <TbListTree aria-hidden="true" /> },
            { value: "expression", label: labels.expressionTab, icon: <TbCode aria-hidden="true" /> },
          ]}
          onChange={(mode) => {
            if (mode === draft.mode) return
            if (mode === "expression") {
              setDraft((current) => ({ ...current, mode }))
              return
            }
            setDraft(draft.incomplete ? { ...draft, mode: "builder" } : formulaDraft(draft.expression))
          }}
        />
        <div className="datox-kpi-formula__history">
          <button
            type="button"
            title={labels.undoFormula}
            aria-label={labels.undoFormula}
            disabled={!history.past.length}
            onClick={() => restore("past")}
          >
            <TbArrowBackUp />
          </button>
          <button
            type="button"
            title={labels.redoFormula}
            aria-label={labels.redoFormula}
            disabled={!history.future.length}
            onClick={() => restore("future")}
          >
            <TbArrowForwardUp />
          </button>
        </div>
      </div>
      <div id={`${id}-panel`} role="tabpanel" aria-labelledby={`${id}-${draft.mode}`}>
        {draft.mode === "builder" ? (
          <>
            <label className="datox-kpi-formula__templates">
              <span>{labels.startFrom}</span>
              <select
                value=""
                className="datox-react-dashboard__select"
                onChange={(event) => changeTree(formulaTemplate(event.currentTarget.value))}
              >
                <option value="" disabled>
                  {labels.chooseTemplate}
                </option>
                <option value="ratio">{labels.ratioTemplate}</option>
                <option value="difference">{labels.differenceTemplate}</option>
                <option value="conditional">{labels.conditionalTemplate}</option>
                <option value="count">{labels.countTemplate}</option>
              </select>
            </label>
            <KpiFormulaNodeEditor node={draft.tree} onChange={changeTree} columns={columns} labels={labels} />
            {draft.incomplete ? (
              <p className="datox-kpi-formula__notice" role="status">
                {labels.completeBuilder}
              </p>
            ) : null}
            {draft.expression ? (
              <details className="datox-kpi-formula__generated">
                <summary>{labels.viewExpression}</summary>
                <code>{draft.expression}</code>
              </details>
            ) : null}
          </>
        ) : (
          <>
            <KpiExpressionEditor
              value={draft.expression}
              columns={columns}
              labels={labels}
              onChange={(expression) => edit(formulaDraft(expression, "expression"))}
            />
            {draft.expression && !draft.tree ? (
              <p className="datox-kpi-formula__notice" role="status">
                {labels.preservedExpression}
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

function KpiFormulaNodeEditor({ node, onChange, columns, labels, depth = 0 }) {
  if (!node) return null
  const kind = node.type === "call" ? node.name.toLowerCase() : node.type
  const blank = () => ({ type: "number", value: "0" })
  const wrap = (type) => {
    if (type === kind) return
    if (type === "aggregate") {
      onChange(formulaTemplate("aggregate"))
      return
    }
    if (type === "number") {
      onChange(blank())
      return
    }
    if (type === "literal") {
      onChange({ type, value: "True" })
      return
    }
    if (type === "binary") {
      onChange({ type, operator: "+", left: node, right: blank() })
      return
    }
    if (type === "unary") {
      onChange({ type, operator: "-", operand: node })
      return
    }
    if (type === "logic") {
      onChange({ type, operator: "and", values: [node, { type: "literal", value: "True" }] })
      return
    }
    onChange({
      type: "call",
      name: type,
      args:
        type === "if_else"
          ? [{ type: "literal", value: "True" }, node, blank()]
          : type === "safe_divide" || type === "round"
            ? [node, { type: "number", value: type === "round" ? "2" : "1" }]
            : [node],
    })
  }
  const child = (value, caption, update) => (
    <div className="datox-kpi-formula__operand">
      <span className="datox-kpi-formula__operand-label">{caption}</span>
      <KpiFormulaNodeEditor node={value} onChange={update} columns={columns} labels={labels} depth={depth + 1} />
    </div>
  )
  return (
    <div
      className={`datox-kpi-formula__node ${depth ? "datox-kpi-formula__node--nested" : ""}`}
      role="group"
      aria-label={labels.formulaTerm}
    >
      <label>
        <span className="datox-kpi-formula__sr-only">{labels.valueType}</span>
        <select
          className="datox-react-dashboard__select"
          value={kind}
          onChange={(event) => wrap(event.currentTarget.value)}
        >
          <option value="aggregate">{labels.aggregateTerm}</option>
          <option value="number">{labels.numberTerm}</option>
          <option value="binary" disabled={depth >= 18}>
            {labels.operationTerm}
          </option>
          <option value="logic" disabled={depth >= 18}>
            {labels.logicTerm}
          </option>
          <option value="literal">{labels.literalTerm}</option>
          <option value="unary" disabled={depth >= 18}>
            {labels.unaryTerm}
          </option>
          {["safe_divide", "round", "abs", "if_else"].map((name) => (
            <option value={name} key={name} disabled={depth >= 18}>
              {labels.functionNames[name]}
            </option>
          ))}
        </select>
      </label>
      {node.type === "aggregate" ? (
        <div className="datox-kpi-formula__aggregate">
          <label>
            <span>{labels.aggregation}</span>
            <select
              className="datox-react-dashboard__select"
              value={node.name.toLowerCase()}
              onChange={(event) =>
                onChange({
                  ...node,
                  name: event.currentTarget.value,
                  column: event.currentTarget.value === "count" ? null : node.column || "",
                  quoted: true,
                })
              }
            >
              {KPI_AGGREGATES.map((name) => (
                <option key={name} value={name}>
                  {labels.functionNames[name]}
                </option>
              ))}
            </select>
          </label>
          {node.name.toLowerCase() !== "count" ? (
            <label>
              <span>{labels.columnLabel}</span>
              <select
                className="datox-react-dashboard__select"
                value={node.column || ""}
                title={node.column || labels.chooseColumn}
                onChange={(event) => onChange({ ...node, column: event.currentTarget.value, quoted: true })}
              >
                <option value="" disabled>
                  {labels.chooseColumn}
                </option>
                {[...new Set([...(node.column ? [node.column] : []), ...columns])].map((column) => (
                  <option key={column} value={column}>
                    {column}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <small>{labels.functions.count}</small>
          )}
        </div>
      ) : null}
      {node.type === "number" ? (
        <label>
          <span>{labels.numberTerm}</span>
          <input
            className="datox-react-dashboard__input"
            inputMode="decimal"
            value={node.value}
            maxLength={1000}
            onChange={(event) => onChange({ ...node, value: event.currentTarget.value })}
          />
        </label>
      ) : null}
      {node.type === "literal" ? (
        <label>
          <span>{labels.literalTerm}</span>
          <select
            className="datox-react-dashboard__select"
            value={node.value}
            onChange={(event) => onChange({ ...node, value: event.currentTarget.value })}
          >
            {["True", "False", "None"].map((value) => (
              <option key={value} value={value}>
                {labels.literals[value]}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {node.type === "binary" ? (
        <>
          {child(node.left, labels.leftValue, (left) => onChange({ ...node, left }))}
          <label>
            <span>{labels.operator}</span>
            <select
              className="datox-react-dashboard__select"
              value={node.operator}
              onChange={(event) => onChange({ ...node, operator: event.currentTarget.value })}
            >
              {["+", "-", "*", "/", ">", ">=", "<", "<=", "==", "!="].map((op) => (
                <option key={op} value={op}>
                  {labels.operators[op]}
                </option>
              ))}
            </select>
          </label>
          {child(node.right, labels.rightValue, (right) => onChange({ ...node, right }))}
        </>
      ) : null}
      {node.type === "unary" ? (
        <>
          <label>
            <span>{labels.operator}</span>
            <select
              className="datox-react-dashboard__select"
              value={node.operator}
              onChange={(event) => onChange({ ...node, operator: event.currentTarget.value })}
            >
              {["+", "-", "not"].map((op) => (
                <option key={op} value={op}>
                  {labels.operators[op]}
                </option>
              ))}
            </select>
          </label>
          {child(node.operand, labels.valueLabel, (operand) => onChange({ ...node, operand }))}
        </>
      ) : null}
      {node.type === "logic" ? (
        <>
          <label>
            <span>{labels.operator}</span>
            <select
              className="datox-react-dashboard__select"
              value={node.operator}
              onChange={(event) => onChange({ ...node, operator: event.currentTarget.value })}
            >
              <option value="and">{labels.allConditions}</option>
              <option value="or">{labels.anyConditions}</option>
            </select>
          </label>
          {node.values.map((value, index) => (
            <React.Fragment key={index}>
              {child(value, labels.condition, (next) =>
                onChange({ ...node, values: node.values.map((entry, i) => (i === index ? next : entry)) }),
              )}
            </React.Fragment>
          ))}
        </>
      ) : null}
      {node.type === "call" ? (
        <div>
          <small className="datox-kpi-formula__help">{labels.functions[node.name.toLowerCase()]}</small>
          {node.args.map((value, index) => (
            <React.Fragment key={index}>
              {child(
                value,
                {
                  safe_divide: [labels.numerator, labels.denominator],
                  round: [labels.valueLabel, labels.precision],
                  abs: [labels.valueLabel],
                  if_else: [labels.condition, labels.thenValue, labels.elseValue],
                }[node.name.toLowerCase()][index],
                (next) => onChange({ ...node, args: node.args.map((entry, i) => (i === index ? next : entry)) }),
              )}
            </React.Fragment>
          ))}
        </div>
      ) : null}
    </div>
  )
}
